import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";
import { Link, useNavigate } from "react-router-dom";

const UnverifiedBuddies = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const navigate = useNavigate();
  const [nonVerifiedProviders, setNonVerifiedProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "active" | "suspended"
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState({});
  const itemsPerPage = 20;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    navigate("/login");
  };

  useEffect(() => {
    const fetchNonVerifiedProviders = async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const response = await fetch(
          `${BASE_URL}admin/providers/not-verified?page=1&limit=500`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const data = await response.json();
        if (data.status === "success") {
          setNonVerifiedProviders(data.data || []);
        } else {
          setErrorMsg(data.message || "Failed to load pending applications");
        }
      } catch (err) {
        console.error("Error fetching non-verified providers:", err);
        setErrorMsg("Network error while loading pending applications");
      }
      setLoading(false);
    };
    fetchNonVerifiedProviders();
  }, []);

  // Filtered list
  const filteredProviders = useMemo(() => {
    return nonVerifiedProviders.filter((provider) => {
      // Status filter
      if (statusFilter === "active" && provider.isSuspended) return false;
      if (statusFilter === "suspended" && !provider.isSuspended) return false;

      // Search filter
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const matchName = provider.name?.toLowerCase().includes(q);
      const matchPhone = provider.phone?.includes(q);
      const matchEmail = provider.email?.toLowerCase().includes(q);
      const matchCity = provider.city?.toLowerCase().includes(q);
      return matchName || matchPhone || matchEmail || matchCity;
    });
  }, [nonVerifiedProviders, statusFilter, searchQuery]);

  // Reset to page 1 on search or filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // Real Database Statistics (No Math.floor / Mock Data)
  const stats = useMemo(() => {
    const total = nonVerifiedProviders.length;
    const active = nonVerifiedProviders.filter((p) => !p.isSuspended).length;
    const suspended = nonVerifiedProviders.filter((p) => p.isSuspended).length;
    const withEmail = nonVerifiedProviders.filter(
      (p) => p.email && p.email.trim() !== "",
    ).length;

    return { total, active, suspended, withEmail };
  }, [nonVerifiedProviders]);

  // Pagination calculations
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProviders.length / itemsPerPage),
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProviders = filteredProviders.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSuspendUnsuspend = async (e, providerId, isSuspended) => {
    e.preventDefault();
    e.stopPropagation();

    setActionLoading((prev) => ({ ...prev, [providerId]: true }));
    setErrorMsg("");

    try {
      const action = isSuspended ? "unsuspend" : "suspend";
      const res = await fetch(
        `${BASE_URL}admin/providers/${providerId}/${action}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const data = await res.json();
      if (data.status === "success") {
        setNonVerifiedProviders((prev) =>
          prev.map((p) =>
            p.id === providerId
              ? {
                  ...p,
                  isSuspended:
                    data.data?.isSuspended !== undefined
                      ? data.data.isSuspended
                      : !isSuspended,
                  status: data.data?.status || p.status,
                }
              : p,
          ),
        );
        setSuccessMsg(
          data.message ||
            `Provider successfully ${isSuspended ? "unsuspended" : "suspended"}.`,
        );
        setTimeout(() => setSuccessMsg(""), 3500);
      } else {
        setErrorMsg(data.message || "Failed to update provider status");
        setTimeout(() => setErrorMsg(""), 3500);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      setErrorMsg("Network error while updating status");
      setTimeout(() => setErrorMsg(""), 3500);
    }

    setActionLoading((prev) => ({ ...prev, [providerId]: false }));
  };

  const getInitials = (name) => {
    if (!name) return "P";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="flex-1 lg:ml-72 min-h-screen flex flex-col">
        {/* Sticky Executive Topbar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-stone-200/80 px-4 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-400">
                <span>Admin</span>
                <span>/</span>
                <span className="text-stone-700">Pending Verification</span>
              </div>
              <h1 className="text-xl font-extrabold text-stone-900 tracking-tight flex items-center gap-2">
                <span>Unverified Buddies</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                  {stats.total} Applications Awaiting Review
                </span>
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/admin/verified-buddies"
                className="px-3.5 py-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
              >
                <span>Verified Buddies Directory</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Body Container */}
        <div className="p-4 sm:p-8 max-w-7xl w-full mx-auto space-y-6">
          {/* Notifications */}
          {successMsg && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-emerald-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{successMsg}</span>
            </div>
          )}

          {errorMsg && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-rose-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Section 1: Real Stats Cards (Zero Mock Data) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Pending Review
                </span>
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                {stats.total}
              </div>
              <div className="text-xs text-amber-700 font-semibold mt-1">
                Awaiting KYC validation
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Active Applicants
                </span>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-emerald-600">
                {stats.active}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Ready for document approval
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Restricted / Suspended
                </span>
                <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-rose-600">
                {stats.suspended}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Onboarding blocked
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Documented Profiles
                </span>
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                {stats.withEmail}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                With registered email ID
              </div>
            </div>
          </div>

          {/* Section 2: Search, Filters & Action Controls */}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              {/* Search Bar */}
              <div className="relative flex-1">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search applicants by name, phone, email, or city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-50 hover:bg-stone-100/60 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#F8D346] transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-xs font-bold"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Status Filter Tabs */}
              <div className="flex items-center gap-1.5 p-1 bg-stone-100 rounded-xl">
                <button
                  onClick={() => setStatusFilter("all")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    statusFilter === "all"
                      ? "bg-white text-stone-900 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  All ({stats.total})
                </button>
                <button
                  onClick={() => setStatusFilter("active")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    statusFilter === "active"
                      ? "bg-white text-stone-900 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  Active ({stats.active})
                </button>
                <button
                  onClick={() => setStatusFilter("suspended")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    statusFilter === "suspended"
                      ? "bg-white text-stone-900 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  Restricted ({stats.suspended})
                </button>
              </div>
            </div>
          </div>

          {/* Section 3: Providers List */}
          <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <div>
                <h3 className="font-black text-stone-900 text-base flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                  Pending Provider Applicants
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Click on any provider to inspect documents and approve
                  verification
                </p>
              </div>
              <span className="text-xs font-bold text-stone-500 bg-stone-100 px-3 py-1 rounded-full">
                Showing {currentProviders.length} of {filteredProviders.length}
              </span>
            </div>

            {loading ? (
              <div className="p-16 text-center">
                <div className="w-10 h-10 border-4 border-[#F8D346] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-sm font-bold text-stone-700">
                  Loading pending buddy applications...
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  Connecting to server registry
                </p>
              </div>
            ) : nonVerifiedProviders.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-black text-stone-900 mb-1">
                  All Caught Up!
                </h4>
                <p className="text-xs text-stone-500 max-w-sm mx-auto">
                  There are no pending provider verification applications at the
                  moment.
                </p>
              </div>
            ) : filteredProviders.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-16 h-16 bg-stone-100 text-stone-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h4 className="text-base font-bold text-stone-800 mb-1">
                  No Matching Applicants Found
                </h4>
                <p className="text-xs text-stone-400">
                  No pending buddy matches the query "{searchQuery}".
                </p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {currentProviders.map((provider) => (
                  <div
                    key={provider.id}
                    className="p-5 hover:bg-stone-50/70 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 group"
                  >
                    <Link
                      to={`/provider/${provider.id}/non-verified`}
                      className="flex items-center gap-4 flex-1 min-w-0"
                    >
                      {/* Circular Avatar */}
                      <div className="relative shrink-0">
                        {provider.profileImage ? (
                          <img
                            src={provider.profileImage}
                            alt={provider.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-stone-200 shadow-sm"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-stone-900 border-2 border-stone-800 shadow-sm flex items-center justify-center text-[#F8D346] font-black text-sm">
                            {getInitials(provider.name)}
                          </div>
                        )}
                        <div
                          className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-amber-400 rounded-full border-2 border-white flex items-center justify-center shadow-xs"
                          title="Pending Approval"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-stone-900"></span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h4 className="text-base font-bold text-stone-900 group-hover:text-amber-700 transition-colors truncate">
                            {provider.name}
                          </h4>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                            ⏳ Pending KYC
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              provider.isSuspended
                                ? "bg-rose-100 text-rose-800 border border-rose-200"
                                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            }`}
                          >
                            {provider.isSuspended ? "Restricted" : "Active"}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-xs text-stone-500">
                          <span className="flex items-center gap-1 font-semibold text-stone-700">
                            <svg
                              className="w-3.5 h-3.5 text-stone-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            </svg>
                            {provider.phone || "No Phone"}
                          </span>
                          {provider.email && (
                            <span className="flex items-center gap-1">
                              <svg
                                className="w-3.5 h-3.5 text-stone-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                              </svg>
                              {provider.email}
                            </span>
                          )}
                          {provider.city && (
                            <span className="flex items-center gap-1">
                              <svg
                                className="w-3.5 h-3.5 text-stone-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              {provider.city}
                            </span>
                          )}
                          <span className="text-[11px] text-stone-400">
                            Registered:{" "}
                            {provider.createdAt
                              ? new Date(provider.createdAt).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </Link>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                      <Link
                        to={`/provider/${provider.id}/non-verified`}
                        className="px-3.5 py-1.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                      >
                        <span>Review & Verify</span>
                        <svg
                          className="w-3.5 h-3.5 text-[#F8D346]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>

                      <button
                        onClick={(e) =>
                          handleSuspendUnsuspend(
                            e,
                            provider.id,
                            provider.isSuspended,
                          )
                        }
                        disabled={actionLoading[provider.id]}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all disabled:opacity-50 ${
                          provider.isSuspended
                            ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                            : "bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"
                        }`}
                      >
                        {actionLoading[provider.id]
                          ? "..."
                          : provider.isSuspended
                            ? "Unsuspend"
                            : "Suspend"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {filteredProviders.length > 0 && (
              <div className="px-6 py-4 border-t border-stone-100 bg-stone-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-xs text-stone-500">
                  Page {currentPage} of {totalPages} • Total{" "}
                  {filteredProviders.length} records
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    ← Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => {
                        return (
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - currentPage) <= 1
                        );
                      })
                      .map((page, idx, arr) => (
                        <React.Fragment key={page}>
                          {idx > 0 && arr[idx - 1] !== page - 1 && (
                            <span className="px-1 text-xs text-stone-400">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                              currentPage === page
                                ? "bg-stone-900 text-[#F8D346] shadow-sm"
                                : "bg-white text-stone-700 border border-stone-200 hover:bg-stone-50"
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UnverifiedBuddies;
